<template>
    <div class="flex flex-column mb-2">
        <p v-if="configs.length == 0">Please add a Perk Config</p>

        <div style="padding-left: 12px;">
            <div v-for="(perkConfig, index) in configs" :key="index" class="field mb-5 border-1 border-gray-300 p-2 border-round shadow-1">
                <label :for="'config-' + index" class="font-bold text-900" style="font-size:90%;margin-bottom: 2px;">Perk {{index + 1}}</label>

                <div class="p-inputgroup">
                    <Dropdown  
                        v-model="perkConfig.id" 
                        :options="perks" 
                        optionLabel="title" 
                        optionValue="id" 
                        :optionDisabled="(perk) => !perk.enabled"
                        filter
                        filterPlaceholder="Search perks"
                        placeholder="Select Perk" />
                    <Button @click="deleteConfig(index)" icon="pi pi-times" class="p-button-warning"/>
                </div>

                <label v-show="!perkConfig.isFromNightPay" :for="'venue-' + index" class="mt-2" style="font-size:90%;margin-bottom: 2px;">Venue</label>
                <div  v-show="!perkConfig.isFromNightPay"  class="p-inputgroup">
                    <Dropdown  
                        v-model="perkConfig.venueId" 
                        :options="venues" 
                        optionLabel="name" 
                        optionValue="id" 
                        filter
                        placeholder="All" />
                </div>
                <label class="mt-2" style="font-size:90%;margin-bottom: 2px;">Is From NightPay</label>
                <div class="p-inputgroup">
                    <InputSwitch  
                        v-model="perkConfig.isFromNightPay" 
                        />
                </div>

                <!-- Time-based Redemption Controls -->
                <div class="mt-4 p-3 border-1 border-gray-200 border-round">
                    <h4 class="text-sm font-semibold mb-2">🕒 Redemption Time Control</h4>
                    
                    <div class="field mb-3">
                        <label class="text-sm font-medium">Limit perk redemption hours</label>
                        <div class="flex align-items-center mt-1">
                            <InputSwitch 
                                v-model="perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited" 
                                @change="onRedemptionTimeLimitedChange(perkConfig)"
                            />
                            <span class="ml-2 text-xs text-gray-600">
                                {{ perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited ? 'Enabled' : 'Disabled' }}
                            </span>
                        </div>
                        <small class="text-xs text-gray-500 mt-1">
                            When enabled, perk redemption will be restricted to specific hours
                        </small>
                    </div>

                    <div v-if="perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited" class="grid">
                        <div class="col-12 md:col-6">
                            <label class="text-sm font-medium">🔒 Locked from time</label>
                            <Calendar
                                v-model="perkConfig.perkRedemptionTimeControl.lockedFromTimeDate"
                                timeOnly
                                hourFormat="24"
                                placeholder="e.g., 22:00"
                                @date-select="onTimeChange(perkConfig, 'lockedFromTime', $event)"
                                class="w-full mt-1"
                                :inputStyle="{ cursor: 'pointer' }"
                                @beforeinput.prevent
                                @input.prevent
                                @keypress.prevent
                                @keydown.prevent
                            />
                            <small class="text-xs text-gray-500">
                                Time when perk becomes unavailable for redemption in {{ detectedTimeZone }}
                            </small>
                        </div>

                        <div class="col-12 md:col-6">
                            <label class="text-sm font-medium">🔓 Unlocked at time</label>
                            <Calendar
                                v-model="perkConfig.perkRedemptionTimeControl.unlockedAtTimeDate"
                                timeOnly
                                hourFormat="24"
                                placeholder="e.g., 10:00"
                                @date-select="onTimeChange(perkConfig, 'unlockedAtTime', $event)"
                                class="w-full mt-1"
                                :inputStyle="{ cursor: 'pointer' }"
                                @beforeinput.prevent
                                @input.prevent
                                @keypress.prevent
                                @keydown.prevent
                            />
                            <small class="text-xs text-gray-500">
                                Time when perk becomes available again in {{ detectedTimeZone }}
                            </small>
                        </div>

                        <div class="col-12 mt-2">
                            <label class="text-sm font-medium">🌍 Timezone</label>
                            <InputText
                                v-model="perkConfig.perkRedemptionTimeControl.timeZoneId"
                                readonly
                                class="w-full mt-1"
                            />
                            <small class="text-xs text-gray-500">
                                Timezone for the redemption time control (auto-detected)
                            </small>
                        </div>
                    </div>
                </div>

            </div>
            <Button @click="addConfig" label="Add Perk" class="mt-2 p-button-secondary p-button-sm" />
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed} from 'vue'

import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'

export default {
    props: [
        'context',
        'field'
    ],
    setup(props) {
        const store = useStore()
        const toast = useToast()

        const loading = ref(true)
        const perks = ref([])
        const venues = ref([])

        const configs = ref(props.context.perkConfigs)

        // Timezone detection
        const detectedTimeZone = computed(() => {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        });

        const currentLocalTime = computed(() => {
            return new Date().toLocaleTimeString('en-US', { 
                timeZone: detectedTimeZone.value, 
                hour12: false 
            });
        });

        function initializeRedemptionTimeControl(config) {
            if (!config.perkRedemptionTimeControl) {
                config.perkRedemptionTimeControl = {
                    isRedemptionTimeLimited: false,
                    lockedFromTime: null,
                    unlockedAtTime: null,
                    lockedFromTimeDate: null,
                    unlockedAtTimeDate: null,
                    timeZoneId: detectedTimeZone.value
                };
            } else {
                // Initialize Date objects for time pickers from string times
                if (config.perkRedemptionTimeControl.lockedFromTime) {
                    config.perkRedemptionTimeControl.lockedFromTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.lockedFromTime);
                }
                if (config.perkRedemptionTimeControl.unlockedAtTime) {
                    config.perkRedemptionTimeControl.unlockedAtTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.unlockedAtTime);
                }
                // Ensure timezone is set
                if (!config.perkRedemptionTimeControl.timeZoneId) {
                    config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
                }
            }
        }

        function parseTimeToDate(timeString) {
            if (!timeString) return null;
            const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
            
            // Create a date object in local time
            const date = new Date();
            date.setHours(hours, minutes, 0, 0);
            
            return date;
        }

        function formatTimeFromDate(date) {
            if (!date) return null;
            
            // Get time in local timezone
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = "00"; // Always use 00 for seconds
            
            return `${hours}:${minutes}:${seconds}`; // TimeSpan format for JSON.NET
        }

        function onRedemptionTimeLimitedChange(config) {
            if (!config.perkRedemptionTimeControl.isRedemptionTimeLimited) {
                // Clear time restrictions when disabled
                config.perkRedemptionTimeControl.lockedFromTime = null;
                config.perkRedemptionTimeControl.lockedFromTimeDate = null;
                config.perkRedemptionTimeControl.unlockedAtTime = null;
                config.perkRedemptionTimeControl.unlockedAtTimeDate = null;
            } else {
                // Set default timezone when enabling
                config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
            }
        }

        function onTimeChange(config, timeType, event) {
            const timeString = formatTimeFromDate(event);
            if (timeType === 'lockedFromTime') {
                config.perkRedemptionTimeControl.lockedFromTime = timeString;
            } else if (timeType === 'unlockedAtTime') {
                config.perkRedemptionTimeControl.unlockedAtTime = timeString;
            }
        }

        function addConfig() {
            const newConfig = { id: null, venueId: null, isFromNightPay: false };
            initializeRedemptionTimeControl(newConfig);
            configs.value.push(newConfig);
        }

        function deleteConfig(index) {
            configs.value.splice(index, 1)
        }

        async function loadRequiredData() {
            loading.value = true

            // Load Perks
            try {
                await store.dispatch('perks/ensurePerkListItems');
            } catch (err) {
                toast.add({severity:'error', summary: 'Error', detail: 'Could not load Perks: ' + err, life: 3000})
            }
            const loadedPerks = store.getters['perks/perkListItems']
            perks.value = loadedPerks

            loadVenues();
            loading.value = false
        }


		async function loadVenues(){
			await store.dispatch('venues/ensureVenueListItems');
			const loadedVenues = store.getters['venues/venueListItems'];
			venues.value = [{id: null, name: "All"}, ...loadedVenues];
            console.log(configs.value)

		}

        onMounted(async () => {
            await loadRequiredData();
            
            // Initialize redemption time control for existing configs
            configs.value?.forEach(config => {
                initializeRedemptionTimeControl(config);
            });
        })

        return {
            perks,
            venues,

            configs,

            addConfig,
            deleteConfig,
            onRedemptionTimeLimitedChange,
            onTimeChange,
            detectedTimeZone,
            currentLocalTime
        }
    },
}
</script>

<style scoped>
.grid {
  display: grid;
  gap: 1rem;
}

.col-12 {
  grid-column: span 12;
}

.md\:col-6 {
  grid-column: span 6;
}

@media (max-width: 768px) {
  .md\:col-6 {
    grid-column: span 12;
  }
}
</style>